/**
 * AI configuration and usage tracking
 * Manages API keys, provider selection, and daily usage quotas
 * Ported from index.html lines 748-786
 */

import { toDateStr } from '@/lib/helpers';

const AI_STORAGE_KEY = 'liftforge_ai_config';
const AI_USAGE_KEY = 'liftforge_ai_usage';

export interface AIConfig {
  provider: string;
  apiKey: string;
  goal?: string;
  consent?: boolean;
}

export interface AIUsage {
  date: string;
  count: number;
}

/**
 * Obfuscate/deobfuscate API keys using XOR with salt
 * Provides minimal protection against casual inspection
 */
function _aiObfuscate(s: string): string {
  const salt = 'liftforge_k';
  return s
    .split('')
    .map((c, i) => String.fromCharCode(c.charCodeAt(0) ^ salt.charCodeAt(i % salt.length)))
    .join('');
}

/**
 * Get AI configuration from localStorage
 * Returns defaults if missing or corrupt
 * Deobfuscates API key on load
 */
export function getAIConfig(): AIConfig {
  try {
    const raw = localStorage.getItem(AI_STORAGE_KEY);
    if (!raw) {
      return {
        provider: 'free',
        apiKey: '',
        goal: 'hypertrophy',
        consent: false,
      };
    }

    const cfg = JSON.parse(raw) as AIConfig;
    if (cfg.apiKey) {
      cfg.apiKey = _aiObfuscate(atob(cfg.apiKey));
    }
    return cfg;
  } catch (e) {
    console.warn('getAIConfig:', e);
    return {
      provider: 'free',
      apiKey: '',
      goal: 'hypertrophy',
      consent: false,
    };
  }
}

/**
 * Save AI configuration to localStorage
 * Obfuscates API key before storage
 */
export function setAIConfig(cfg: AIConfig): void {
  try {
    const toSave = { ...cfg };
    if (toSave.apiKey) {
      toSave.apiKey = btoa(_aiObfuscate(toSave.apiKey));
    }
    localStorage.setItem(AI_STORAGE_KEY, JSON.stringify(toSave));
  } catch (e) {
    console.warn('setAIConfig:', e);
  }
}

/**
 * Get daily AI usage count
 * Resets count if date has changed
 */
export function getAIUsage(): AIUsage {
  const today = toDateStr(new Date());
  try {
    const raw = localStorage.getItem(AI_USAGE_KEY);
    if (!raw) {
      return { date: today, count: 0 };
    }

    const u = JSON.parse(raw) as AIUsage;
    if (u.date !== today) {
      return { date: today, count: 0 };
    }
    return u;
  } catch (e) {
    console.warn('getAIUsage:', e);
    return { date: today, count: 0 };
  }
}

/**
 * Increment daily AI usage counter
 * Returns updated usage for display/quota checking
 */
export function incrementAIUsage(): AIUsage {
  const u = getAIUsage();
  u.count++;
  try {
    localStorage.setItem(AI_USAGE_KEY, JSON.stringify(u));
  } catch (e) {
    console.warn('incrementAIUsage:', e);
  }
  return u;
}

/**
 * Reset usage for a specific date (testing/admin only)
 */
export function resetAIUsage(date?: string): void {
  const resetDate = date || toDateStr(new Date());
  try {
    localStorage.removeItem(AI_USAGE_KEY);
  } catch (e) {
    console.warn('resetAIUsage:', e);
  }
}
